<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <header class="modal-header">
        <h3>{{ $t('plan_trail.day_edit_modal.edit_day_day_number', { day_number: dayNumber }) }}</h3>
        <button class="close-btn" @click="close" :aria-label="$t('shared.close')">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </header>
      
      <div class="modal-body">
        <div class="form-group">
          <label class="text-muted mb-1" for="day-name">{{ $t('shared.name') }}</label>
          <input
            id="day-name"
            v-model="localName"
            type="text"
            class="form-control"
            :placeholder="$t('plan_trail.day_edit_modal.day_number', { number: dayNumber })"
            @keydown.enter.prevent="save"
          />
        </div>
        
        <div class="form-group">
          <label for="day-description" class="text-muted mb-1">{{ $t('shared.description') }}</label>
          <textarea
            id="day-description"
            v-model="localDescription"
            class="form-control"
            rows="4"
            :placeholder="$t('plan_trail.day_edit_modal.day_notes_placeholder')"
          />
        </div>
      </div>
      
      <footer class="modal-footer">
        <button class="btn btn-secondary" @click="close">{{ $t('shared.cancel_2') }}</button>
        <button class="btn btn-primary" @click="save">{{ $t('shared.save') }}</button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  dayNumber: {
    type: Number,
    required: true
  },
  name: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'save']);

const localName = ref('');
const localDescription = ref('');

// Update local values when props change
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    localName.value = props.name || '';
    localDescription.value = props.description || '';
  }
});

function handleOverlayClick(event) {
  if (event.target === event.currentTarget) {
    close();
  }
}

function close() {
  emit('close');
}

function save() {
  emit('save', {
    dayNumber: props.dayNumber,
    name: localName.value,
    description: localDescription.value
  });
  close();
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--dark);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #fff;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border: none;
}
</style>